import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { TenantProvider } from './context/TenantContext.jsx';
import Layout from './components/Layout.jsx';
import SessionNavigationTracker from './components/SessionNavigationTracker.jsx';
import {
    getLastTenantSlug,
    rememberReturnPath,
    resolveLoginPath,
    resolvePostLoginPath,
} from './utils/sessionNavigation.js';
import Login from './pages/Login.jsx';
import PaginaKiosko from './pages/PaginaKiosko.jsx';
import TrabajadorDashboard from './pages/TrabajadorDashboard.jsx';
import SupervisorDashboard from './pages/SupervisorDashboard.jsx';
import TrabajadorDetalle from './pages/TrabajadorDetalle.jsx';
import AdminDashboard from './pages/AdminDashboard.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Survey from './pages/Survey.jsx';
import AdminDiagnostico from './pages/AdminDiagnostico.jsx';
import WorkerAccount from './pages/WorkerAccount.jsx';
import Alerts from './pages/Alerts.jsx';
import Messages from './pages/Messages.jsx';
import Team from './pages/Team.jsx';
import Epa from './pages/Epa.jsx';
import Calculator from './pages/Calculator.jsx';
import Coach from './pages/Coach.jsx';
import Indicators from './pages/Indicators.jsx';
import FrictionMap from './pages/FrictionMap.jsx';
import AnalyticalBoards from './pages/AnalyticalBoards.jsx';
import Observador from './pages/Observador.jsx';
import CuestionarioPerfil from './pages/CuestionarioPerfil.jsx';
import UserManagement from './pages/Admin/UserManagement.jsx';
import EmailReports from './pages/Admin/EmailReports.jsx';
import RolesManagement from './pages/Admin/RolesManagement.jsx';
import OpsDashboard from './pages/OpsDashboard.jsx';
import SuperadminTenants from './pages/SuperadminTenants.jsx';
import SuperadminOnboarding from './pages/SuperadminOnboarding.jsx';
import SuperadminFinance from './pages/SuperadminFinance.jsx';
import TermsOfService from './pages/Legal/TermsOfService.jsx';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy.jsx';
import ApoyosHub from './pages/ApoyosHub.jsx';
import AdminJornadaPreview from './pages/AdminJornadaPreview.jsx';
import GlosarioALINEA from './pages/GlosarioALINEA.jsx';
import JornadaGuia from './pages/JornadaGuia.jsx';
import LegacyBottomNavCleanup from './components/LegacyBottomNavCleanup.jsx';
import { effectiveAppRole } from './utils/roles.js';

function ProtectedRoute({ children, roles }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="spinner" />
            </div>
        );
    }

    if (!user) {
        const returnTo = `${location.pathname}${location.search}`;
        rememberReturnPath(returnTo);
        return <Navigate to={resolveLoginPath({ fallbackSlug: getLastTenantSlug() })} replace />;
    }

    const appRole = effectiveAppRole(user.rol);

    // Superadmin has access to everything
    const hasAccess = !roles || roles.includes(appRole) || user.rol === 'superadmin';
    if (!hasAccess) return <Navigate to="/" replace />;

    return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to={resolvePostLoginPath(user)} replace />;
    return children;
}

function DashboardDispatcher() {
    const { user } = useAuth();
    if (effectiveAppRole(user?.rol) === 'worker') return <TrabajadorDashboard />;
    if (user?.rol === 'admin' || user?.rol === 'superadmin') return <AdminDashboard />;
    return <SupervisorDashboard />;
}

function SurveyDispatcher() {
    const { user } = useAuth();
    if (user?.rol === 'admin' || user?.rol === 'superadmin') {
        return <Navigate to="/admin/diagnostico" replace />;
    }
    return <Survey />;
}

export default function App() {
    return (
        <BrowserRouter>
            <TenantProvider>
                <AuthProvider>
                    <SessionNavigationTracker />
                    <LegacyBottomNavCleanup />
                    <Routes>
                        {/* Public */}
                        <Route path="/login/:tenantSlug" element={<PublicRoute><Login /></PublicRoute>} />
                        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                        <Route path="/kiosko" element={<PublicRoute><PaginaKiosko /></PublicRoute>} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />

                        {/* All authenticated users */}
                        <Route path="/" element={
                            <ProtectedRoute>
                                <DashboardDispatcher />
                            </ProtectedRoute>
                        } />
                        <Route
                            path="/jornada-guia"
                            element={
                                <ProtectedRoute roles={['worker']}>
                                    <JornadaGuia />
                                </ProtectedRoute>
                            }
                        />

                        {/* Worker routes */}
                        <Route path="/survey" element={<ProtectedRoute roles={['worker', 'admin']}><SurveyDispatcher /></ProtectedRoute>} />
                        <Route path="/profile-quiz" element={<ProtectedRoute roles={['worker', 'admin', 'supervisor']}><CuestionarioPerfil /></ProtectedRoute>} />
                        <Route path="/admin/diagnostico" element={<ProtectedRoute roles={['admin', 'superadmin']}><AdminDiagnostico /></ProtectedRoute>} />
                        <Route
                            path="/preview/jornada-operario"
                            element={
                                <ProtectedRoute roles={['admin', 'superadmin']}>
                                    <AdminJornadaPreview />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/admin/glosario" element={<ProtectedRoute roles={['admin', 'superadmin']}><GlosarioALINEA /></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute roles={['worker', 'admin']}><WorkerAccount /></ProtectedRoute>} />
                        <Route path="/messages" element={<ProtectedRoute roles={['worker', 'admin', 'superadmin', 'supervisor']}><Messages /></ProtectedRoute>} />
                        <Route path="/epa" element={<ProtectedRoute roles={['worker', 'admin', 'supervisor', 'superadmin']}><Epa /></ProtectedRoute>} />
                        <Route path="/observador" element={<ProtectedRoute roles={['worker', 'supervisor']}><Observador /></ProtectedRoute>} />
                        <Route
                            path="/apoyos"
                            element={
                                <ProtectedRoute roles={['worker', 'supervisor']}>
                                    <ApoyosHub />
                                </ProtectedRoute>
                            }
                        />

                        {/* Supervisor routes */}
                        <Route path="/coach" element={<ProtectedRoute roles={['supervisor', 'admin']}><Coach /></ProtectedRoute>} />

                        {/* Supervisor + Admin */}
                        <Route path="/team" element={<ProtectedRoute roles={['supervisor', 'admin']}><Team /></ProtectedRoute>} />
                        <Route path="/team/:id" element={<ProtectedRoute roles={['supervisor', 'admin']}><TrabajadorDetalle /></ProtectedRoute>} />
                        <Route path="/alerts" element={<ProtectedRoute roles={['supervisor', 'admin']}><Alerts /></ProtectedRoute>} />
                        <Route path="/friction-map" element={<ProtectedRoute roles={['admin', 'superadmin']}><FrictionMap /></ProtectedRoute>} />
                        <Route path="/analytical-boards" element={<ProtectedRoute roles={['admin', 'supervisor', 'superadmin']}><AnalyticalBoards /></ProtectedRoute>} />

                        {/* Admin only */}
                        <Route path="/calculator" element={<ProtectedRoute roles={['admin', 'superadmin']}><Calculator /></ProtectedRoute>} />
                        <Route path="/admin/messages" element={<Navigate to="/messages?tab=galeria" replace />} />
                        <Route path="/indicators" element={<ProtectedRoute roles={['admin', 'superadmin']}><Indicators /></ProtectedRoute>} />
                        <Route path="/admin/users" element={<ProtectedRoute roles={['admin', 'superadmin']}><UserManagement /></ProtectedRoute>} />
                        <Route path="/admin/email-reports" element={<ProtectedRoute roles={['admin', 'superadmin']}><EmailReports /></ProtectedRoute>} />
                        <Route path="/admin/roles" element={<ProtectedRoute roles={['admin', 'superadmin']}><RolesManagement /></ProtectedRoute>} />
                        <Route path="/admin/ops" element={<ProtectedRoute roles={['superadmin']}><OpsDashboard /></ProtectedRoute>} />
                        <Route path="/admin/tenants" element={<ProtectedRoute roles={['superadmin']}><SuperadminTenants /></ProtectedRoute>} />
                        <Route path="/admin/finance" element={<ProtectedRoute roles={['superadmin']}><SuperadminFinance /></ProtectedRoute>} />
                        <Route path="/admin/onboarding" element={<ProtectedRoute roles={['superadmin']}><SuperadminOnboarding /></ProtectedRoute>} />

                        {/* Fallback */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AuthProvider>
            </TenantProvider>
        </BrowserRouter>
    );
}
